import { api } from '@goaltree/backend/convex/_generated/api'
import type { LiveItem } from '@goaltree/backend/convex/lib/livePreview'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { ChevronDown, Mic, Pause, Play, Quote, RotateCcw, Sparkles, Zap } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppSidebar } from '@/components/app-sidebar'
import { CaptureCard, CaptureCardMotion, LiveCaptureItems } from '@/components/capture-card'
import { CoachLauncherFace } from '@/components/coach-avatar'
import GoalBuildLoader from '@/components/goal-build-loader'
import { ImageDropHint, ImagePickButton, useImagePasteDrop } from '@/components/image-capture'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import {
  DoneButton,
  LiveTranscript,
  RecordingStatus,
  StuckPrompts,
  VOICE_STARTERS,
  starterPhrase,
} from '@/components/quick-capture'
import { SiteHeader } from '@/components/site-header'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { useScrollTitle } from '@/hooks/use-scroll-title'
import { useStickToEnd } from '@/hooks/use-stick-to-end'
import type { CaptureItem, CaptureResult } from '@/hooks/use-voice-goals'
import { isVoiceCaptureSupported, useVoiceCapture } from '@/hooks/use-voice-goals'
import { countItems, countSummary, formatDuration } from '@/lib/capture'
import { goalUrl } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/voice')({
  head: () => ({
    meta: [{ title: 'Brain dump — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

// ── Ready state ───────────────────────────────────────────────────────────────

function StarterCards({ onPick }: { onPick: (phrase: string) => void }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2">
      {VOICE_STARTERS.map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => onPick(starterPhrase(t(`voice.${key}`)))}
          className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left text-sm text-card-foreground transition-colors hover:bg-muted/40"
        >
          <Quote className="h-3.5 w-3.5 shrink-0 text-primary-text" />
          {t(`voice.${key}`)}
        </button>
      ))}
    </div>
  )
}

function RecentCaptures() {
  const { t, i18n } = useTranslation()
  const captures = useQuery(api.nodes.recentCaptures)
  if (!captures?.length) return null

  const today = new Date()
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()
  const timeFmt = new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' })
  const dateFmt = new Intl.DateTimeFormat(i18n.language, { month: 'short', day: 'numeric' })

  return (
    <div className="mt-8">
      <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-foreground/30">
        {t('voice.recentCaptures')}
      </p>
      <ul className="divide-y divide-border">
        {captures.map((c) => {
          const d = new Date(c._creationTime)
          const day = sameDay(d, today)
            ? t('nav.periodToday')
            : sameDay(d, yesterday)
              ? t('nav.periodYesterday')
              : dateFmt.format(d)
          return (
            <li key={c._id} className="flex items-baseline gap-2 py-2.5 text-sm">
              <span className="shrink-0 text-muted-foreground">
                {day}, {timeFmt.format(d)}
              </span>
              <span className="text-muted-foreground/50">—</span>
              <span className="min-w-0 truncate text-foreground">{countSummary(t, c)}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function ReadyView({
  errorMessage,
  onStart,
  onSubmitText,
  onImage,
  voiceSupported,
}: {
  errorMessage: string | null
  onStart: (prefix?: string) => void
  onSubmitText: (text: string) => void
  onImage: (file: File) => void
  // Browsers without speech recognition still get the typed brain dump.
  voiceSupported: boolean
}) {
  const { t } = useTranslation()
  const [typing, setTyping] = useState(!voiceSupported)
  const [text, setText] = useState('')
  const useStarter = (phrase: string) => {
    if (voiceSupported) onStart(phrase)
    else {
      setTyping(true)
      setText((cur) => (cur.trim() ? cur : `${phrase} `))
    }
  }

  return (
    <>
      <div className="flex-1">
        <p className="mb-6 text-sm leading-relaxed text-muted-foreground">{t('voice.lead')}</p>
        {errorMessage && <p className="mb-4 text-sm text-destructive">{errorMessage}</p>}
        <StarterCards onPick={useStarter} />
        <RecentCaptures />
      </div>

      <div className="sticky bottom-16 z-10 -mx-4 lg:bottom-0 mt-8 bg-background/95 px-4 pt-4 pb-2 backdrop-blur">
        {typing ? (
          <div className="space-y-3">
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('voice.typePlaceholder')}
              rows={5}
              className="w-full resize-none rounded-xl border border-border bg-card px-4 py-3 text-base text-card-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
            />
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-4">
                {voiceSupported ? (
                  <button
                    type="button"
                    onClick={() => setTyping(false)}
                    className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <Mic className="h-4 w-4" />
                    {t('voice.useVoice')}
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground">{t('voice.typeOnlyNote')}</span>
                )}
                <ImagePickButton onImage={onImage} />
              </span>
              <button
                type="button"
                disabled={!text.trim()}
                onClick={() => onSubmitText(text)}
                className="cursor-pointer rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('voice.continue')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <p className="text-xs text-muted-foreground">{t('voice.dockHint')}</p>
            <button
              type="button"
              onClick={() => onStart()}
              aria-label={t('voice.startRecording')}
              className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-full bg-primary transition-all duration-200 hover:bg-primary/90 active:scale-95"
            >
              <Mic className="h-9 w-9 text-white dark:text-[#0a0a0a]" strokeWidth={2} />
            </button>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setTyping(true)}
                className="cursor-pointer text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {t('voice.typeInstead')}
              </button>
              <ImagePickButton onImage={onImage} />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// ── Recording state ───────────────────────────────────────────────────────────

function RecordingView({
  transcript,
  liveItems,
  elapsedMs,
  wrapUpIn,
  paused,
  onActivity,
  onCancel,
  onPrompt,
  onStartOver,
  onStop,
  onTogglePause,
}: {
  transcript: string
  liveItems: LiveItem[]
  elapsedMs: number
  wrapUpIn: number | null
  paused: boolean
  onActivity: () => void
  onCancel: () => void
  onPrompt: (phrase: string) => void
  onStartOver: () => void
  onStop: () => void
  onTogglePause: () => void
}) {
  const { t } = useTranslation()
  const endRef = useRef<HTMLDivElement>(null)
  useStickToEnd(endRef, transcript)

  return (
    <div className="flex flex-1 flex-col" onPointerDown={onActivity}>
      <div className="mb-6 flex items-center justify-between gap-3">
        <p className="shrink-0 text-xs font-semibold uppercase tracking-widest text-foreground/30">
          {t('voice.title')}
        </p>
        <RecordingStatus
          paused={paused}
          elapsedMs={elapsedMs}
          activity={transcript}
          wrapUpIn={wrapUpIn}
        />
      </div>

      <div className="flex-1">
        {transcript ? (
          <>
            <LiveTranscript text={transcript} />
            <LiveCaptureItems items={liveItems} dense={false} />
            <StuckPrompts onPick={onPrompt} className="mt-6" />
          </>
        ) : (
          <>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">{t('voice.lead')}</p>
            <StarterCards onPick={onPrompt} />
          </>
        )}
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-16 z-10 -mx-4 lg:bottom-0 mt-8 flex items-center gap-4 bg-background/95 px-4 pt-4 pb-2 backdrop-blur">
        <button
          type="button"
          onClick={onTogglePause}
          aria-label={paused ? t('voice.resume') : t('voice.pause')}
          className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          <span className="hidden sm:inline">{paused ? t('voice.resume') : t('voice.pause')}</span>
        </button>
        <button
          type="button"
          onClick={onStartOver}
          aria-label={t('voice.startOver')}
          className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <RotateCcw className="h-4 w-4" />
          <span className="hidden sm:inline">{t('voice.startOver')}</span>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto cursor-pointer text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {t('voice.cancel')}
        </button>
        <DoneButton paused={false} onDone={onStop} onResume={onTogglePause} />
      </div>
    </div>
  )
}

// ── Review state ──────────────────────────────────────────────────────────────

function ReviewView({
  items,
  transcript,
  spokenMs,
  fromImage,
  onToggle,
  onRename,
  onWhen,
  onDiscard,
  onCreate,
}: {
  items: CaptureItem[]
  transcript: string
  spokenMs: number | null
  fromImage: boolean
  onToggle: (key: number, selected?: boolean) => void
  onRename: (key: number, title: string) => void
  onWhen: (key: number, when: CaptureItem['when']) => void
  onDiscard: () => void
  onCreate: () => Promise<CaptureResult>
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showTranscript, setShowTranscript] = useState(false)
  const [creating, setCreating] = useState(false)
  const [result, setResult] = useState<CaptureResult | null>(null)

  const selectedCount = items.filter(
    (i) => i.selected && i.status === 'pending' && i.title.trim(),
  ).length
  const summary = countSummary(t, countItems(items))

  async function handleCreate() {
    setCreating(true)
    const counts = await onCreate()
    setCreating(false)
    setResult(counts)
    const created = countSummary(t, counts)
    if (created) toast.success(t('voice.createdToast', { summary: created }))
  }

  if (items.some((i) => i.kind === 'goal' && i.status === 'creating')) return <GoalBuildLoader />

  return (
    <>
      <div className="flex-1">
        <h2 className="text-xl font-black text-foreground">{t('voice.reviewTitle')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {spokenMs !== null
            ? t('voice.fromTalking', { duration: formatDuration(t, spokenMs), summary })
            : fromImage
              ? t('voice.fromImageSummary', { summary })
              : summary}
        </p>
        {transcript && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowTranscript((v) => !v)}
              className="flex cursor-pointer items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronDown
                className={cn('h-3.5 w-3.5 transition-transform', showTranscript && 'rotate-180')}
              />
              {showTranscript ? t('voice.hideTranscript') : t('voice.showTranscript')}
            </button>
            {showTranscript && (
              <p className="mt-2 rounded-lg bg-muted/50 px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {transcript}
              </p>
            )}
          </div>
        )}

        <ul className="mt-5 space-y-2.5">
          {items.map((item) => (
            <CaptureCardMotion key={item.key} layoutKey={item.liveKey} appear={!item.liveKey}>
              <CaptureCard
                item={item}
                relatedEvent={
                  item.relatedEventIndex === undefined
                    ? null
                    : items.find((i) => i.key === item.relatedEventIndex)
                }
                disabled={creating}
                onToggle={(selected) => onToggle(item.key, selected)}
                onRename={(title) => onRename(item.key, title)}
                onWhen={(when) => onWhen(item.key, when)}
                onOpen={
                  item.goalId
                    ? () => void navigate({ to: goalUrl(item.title, item.goalId as string) })
                    : undefined
                }
              />
            </CaptureCardMotion>
          ))}
        </ul>
      </div>

      <div className="sticky bottom-16 z-10 -mx-4 lg:bottom-0 mt-6 flex gap-3 bg-background/95 px-4 pt-4 pb-2 backdrop-blur">
        {result ? (
          <>
            {result.goals > 0 && (
              <button
                type="button"
                onClick={() => void navigate({ to: '/goals' })}
                className="flex-1 cursor-pointer rounded-full bg-primary py-3.5 text-center text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99]"
              >
                {t('voice.viewGoals')}
              </button>
            )}
            {result.tasks + result.habits > 0 && (
              <button
                type="button"
                onClick={() => void navigate({ to: '/everyday' })}
                className={cn(
                  'flex-1 cursor-pointer rounded-full py-3.5 text-center text-sm font-bold transition-all active:scale-[0.99]',
                  result.goals > 0
                    ? 'border border-border text-foreground hover:bg-muted/40'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90',
                )}
              >
                {t('voice.openInbox')}
              </button>
            )}
            <button
              type="button"
              onClick={onDiscard}
              className="cursor-pointer rounded-full border border-border px-5 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted/40"
            >
              {t('voice.done')}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onDiscard}
              disabled={creating}
              className="cursor-pointer rounded-full border border-border px-6 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('voice.discard')}
            </button>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating || selectedCount === 0}
              className="flex-1 cursor-pointer rounded-full bg-primary py-3.5 text-center text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {creating ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {t('voice.creating')}
                </span>
              ) : (
                t('voice.createCount', { count: selectedCount })
              )}
            </button>
          </>
        )}
      </div>
    </>
  )
}

// ── Pro gate ──────────────────────────────────────────────────────────────────

function ProGate() {
  const { t } = useTranslation()
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  return (
    <>
      <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4 pb-16">
        <div className="flex flex-col items-center gap-3">
          <div className="mb-1 flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10">
            <Mic className="h-9 w-9 text-primary-text" />
          </div>
          <h2 className="text-center text-2xl font-black text-foreground">
            {t('voice.unlockTitle')}
          </h2>
          <p className="max-w-xs text-center text-sm leading-relaxed text-foreground/50">
            {t('voice.unlockDesc')}
          </p>
        </div>

        <div className="w-full max-w-xs space-y-3">
          <ProFeature icon={<Mic className="h-4 w-4 text-primary-text" />}>
            {t('voice.proFeature1')}
          </ProFeature>
          <ProFeature icon={<Sparkles className="h-4 w-4 text-primary-text" />}>
            {t('voice.proFeature2')}
          </ProFeature>
          <ProFeature icon={<Zap className="h-4 w-4 text-primary-text" />}>
            {t('voice.proFeature3')}
          </ProFeature>
        </div>

        <button
          type="button"
          onClick={() => setUpgradeOpen(true)}
          className="cursor-pointer w-full max-w-xs rounded-full bg-primary px-8 py-3.5 text-center text-base font-bold text-white dark:text-[#0a0a0a] transition-all hover:scale-[1.01] hover:bg-primary/90 active:scale-[0.99]"
        >
          {t('voice.unlockBtn')}
        </button>
      </div>

      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="voice" />
    </>
  )
}

function ProFeature({ icon, children }: { icon: React.ReactNode; children: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-foreground/[0.06] bg-foreground/[0.03] px-4 py-3">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
        {icon}
      </div>
      <p className="flex-1 text-sm text-foreground/70">{children}</p>
    </div>
  )
}

// ── Main voice page ───────────────────────────────────────────────────────────

function VoicePage() {
  const { t } = useTranslation()
  const posthog = usePostHog()
  const voice = useVoiceCapture()
  const { status } = voice
  const isRecording = status === 'listening' || status === 'paused'

  useEffect(() => {
    if (status === 'review') {
      const c = countItems(voice.items)
      posthog.capture('voice_goals_extracted', { count: voice.items.length, ...c })
    }
  }, [status]) // eslint-disable-line react-hooks/exhaustive-deps

  function start(prefix?: string) {
    posthog.capture('voice_capture_started', { starter: !!prefix })
    voice.start(prefix)
  }

  async function create() {
    const { counts } = await voice.createSelected()
    posthog.capture('voice_capture_created', {
      ...counts,
      ...(voice.fromImage && { source: 'image' }),
    })
    return counts
  }

  function pickImage(file: File) {
    posthog.capture('voice_capture_started', { source: 'image' })
    void voice.submitImage(file)
  }

  const dragging = useImagePasteDrop(pickImage, status !== 'processing' && status !== 'review')

  return (
    <div className="mx-auto flex min-h-full w-full max-w-xl flex-1 flex-col px-4 pt-4 pb-4">
      {!isRecording && (
        <div className="mb-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('voice.capture')}
          </p>
          {status !== 'review' && (
            <h1 className="text-3xl font-black text-foreground">{t('voice.title')}</h1>
          )}
        </div>
      )}

      {dragging ? (
        <ImageDropHint className="mt-4 flex-1" />
      ) : isRecording ? (
        <RecordingView
          transcript={voice.transcript}
          liveItems={voice.liveItems}
          elapsedMs={voice.elapsedMs}
          wrapUpIn={voice.wrapUpIn}
          paused={status === 'paused'}
          onActivity={voice.nudge}
          onCancel={voice.reset}
          onPrompt={voice.addPrompt}
          onStartOver={() => voice.start()}
          onStop={voice.stop}
          onTogglePause={status === 'paused' ? voice.resume : voice.pause}
        />
      ) : status === 'processing' ? (
        <div className="flex flex-1 flex-col">
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            {t(voice.fromImage ? 'voice.readingImage' : 'voice.extracting')}
          </div>
          {voice.liveItems.length > 0 ? (
            <LiveCaptureItems items={voice.liveItems} dense={false} />
          ) : (
            voice.transcript && (
              <p className="mt-6 text-[17px] leading-relaxed whitespace-pre-wrap text-foreground/40">
                {voice.transcript}
              </p>
            )
          )}
        </div>
      ) : status === 'review' ? (
        <ReviewView
          items={voice.items}
          transcript={voice.transcript}
          spokenMs={voice.spokenMs}
          fromImage={voice.fromImage}
          onToggle={voice.toggle}
          onRename={voice.rename}
          onWhen={voice.setWhen}
          onDiscard={voice.reset}
          onCreate={create}
        />
      ) : (
        <ReadyView
          errorMessage={status === 'error' ? voice.errorMessage : null}
          onStart={start}
          onSubmitText={voice.submitText}
          onImage={pickImage}
          voiceSupported={isVoiceCaptureSupported()}
        />
      )}
    </div>
  )
}

// ── Route component ───────────────────────────────────────────────────────────

function RouteComponent() {
  const { t } = useTranslation()
  const { onScroll } = useScrollTitle(t('nav.voice'))
  const { isPro, isLoading } = useIsPro()
  // Remember last resolved value so VoicePage never unmounts on reconnect
  const resolvedProRef = useRef<boolean | null>(null)
  if (!isLoading) resolvedProRef.current = isPro
  const resolvedIsPro = resolvedProRef.current

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader title={t('nav.voice')} showInbox={false} showCoach />
        <SiteHeader
          className="hidden lg:flex"
          rightSlot={
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('open-coach'))}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t('coach.title')}
              title={t('coach.title')}
            >
              <CoachLauncherFace size={20} />
            </button>
          }
        />
        <div className="flex flex-1 flex-col overflow-y-auto pb-16 lg:pb-0" onScroll={onScroll}>
          {resolvedIsPro === null ? null : !resolvedIsPro ? <ProGate /> : <VoicePage />}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
